import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Browse = ({ setCurrentAudio }) => {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [filter, setFilter] = useState('all');
    const [wishlistIds, setWishlistIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        fetchProducts();
        if (isAuthenticated) {
            fetchWishlist();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (filter === 'all') {
            setFilteredProducts(products);
        } else {
            setFilteredProducts(products.filter(p => p.type === filter));
        }
    }, [filter, products]);

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${API}/products`);
            setProducts(response.data);
            setFilteredProducts(response.data);
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const fetchWishlist = async () => {
        try {
            const response = await axios.get(`${API}/wishlist`);
            setWishlistIds(response.data.products.map(p => p.id));
        } catch (error) {
            console.error('Error fetching wishlist:', error);
        }
    };

    const handleAddToCart = async (productId) => {
        try {
            await axios.post(`${API}/cart`, { product_id: productId });
            toast.success('Added to cart!');
        } catch (error) {
            toast.error('Failed to add to cart');
        }
    };

    const handleAddToWishlist = async (productId) => {
        try {
            if (wishlistIds.includes(productId)) {
                await axios.delete(`${API}/wishlist/${productId}`);
                setWishlistIds(wishlistIds.filter(id => id !== productId));
                toast.success('Removed from wishlist');
            } else {
                await axios.post(`${API}/wishlist/${productId}`);
                setWishlistIds([...wishlistIds, productId]);
                toast.success('Added to wishlist!');
            }
        } catch (error) {
            toast.error('Failed to update wishlist');
        }
    };

    const handlePlayAudio = (product) => {
        setCurrentAudio({
            url: product.audio_url,
            name: product.name
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-16">
                <div className="text-xl text-muted-foreground">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-24 px-6 md:px-12 lg:px-24 py-12" data-testid="browse-page">
            <div className="mb-12">
                <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-4">Browse Catalog</h1>
                <p className="text-lg text-muted-foreground">Explore our complete collection</p>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4 mb-12 overflow-x-auto pb-2" data-testid="filter-section">
                <Filter className="w-5 h-5 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                <button 
                    onClick={() => setFilter('all')}
                    className={`px-6 py-2 border transition-colors whitespace-nowrap ${filter === 'all' ? 'border-primary bg-primary text-white' : 'border-white/20 hover:border-white/40'}`}
                    data-testid="filter-all"
                >
                    All
                </button>
                <button 
                    onClick={() => setFilter('sample_pack')}
                    className={`px-6 py-2 border transition-colors whitespace-nowrap ${filter === 'sample_pack' ? 'border-primary bg-primary text-white' : 'border-white/20 hover:border-white/40'}`}
                    data-testid="filter-sample-pack"
                >
                    Sample Packs
                </button>
                <button 
                    onClick={() => setFilter('song')}
                    className={`px-6 py-2 border transition-colors whitespace-nowrap ${filter === 'song' ? 'border-primary bg-primary text-white' : 'border-white/20 hover:border-white/40'}`}
                    data-testid="filter-song"
                >
                    Songs
                </button>
                <button 
                    onClick={() => setFilter('album')}
                    className={`px-6 py-2 border transition-colors whitespace-nowrap ${filter === 'album' ? 'border-primary bg-primary text-white' : 'border-white/20 hover:border-white/40'}`}
                    data-testid="filter-album"
                >
                    Albums
                </button>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="products-grid">
                {filteredProducts.map((product) => (
                    <ProductCard 
                        key={product.id}
                        product={product}
                        onAddToCart={handleAddToCart}
                        onAddToWishlist={handleAddToWishlist}
                        onPlayAudio={handlePlayAudio}
                        isInWishlist={wishlistIds.includes(product.id)}
                    />
                ))}
            </div>

            {filteredProducts.length === 0 && (
                <div className="text-center py-24" data-testid="no-products-message">
                    <p className="text-xl text-muted-foreground">No products found</p>
                </div>
            )}
        </div>
    );
};

export default Browse;
